/**
 * The demo's side of the W2 configuration bundle (`acs_service/config_bundle.py`).
 *
 * The page never judges a bundle — the façade does — so a demo that is to exercise the page has to
 * answer the five doors the way the façade does. What this file ports, and what it deliberately does not:
 *
 * - **The digest is a real SHA-256 over the same canonical JSON** (`sort_keys`, `ensure_ascii=False`,
 *   compact separators) of `{format, version, sections}`. It is hand-written because the demo must stay
 *   synchronous inside the provider's idempotency window and WebCrypto is asynchronous (and absent in
 *   some test runtimes); `test/configFixture.test.ts` pins it to values computed by the server's own
 *   `digest_of`, so a bundle exported here and one exported there are the same bytes for the same
 *   sections. Integers, strings, booleans, nulls, arrays and objects round-trip identically; a float
 *   that Python would print as `1.0` is the one shape this does not claim (no section carries one).
 * - **The planners judge with the validators the demo's own doors already use** (`settingsRejection`,
 *   `validateMatrixWrite`, `validateFixtureProfile`) — the same "no second write path" rule the server
 *   keeps, so a bundle can never put the demo in a state its single-purpose doors would refuse.
 *   The one server rule the demo's settings door does not carry is the optical-pair coherence check
 *   (`optical.check_pair`); the planner stays equal to the door it applies through rather than
 *   getting ahead of it.
 * - **No capability-based omission.** The server omits (export) or refuses (`not-permitted`) a section
 *   whose key the caller lacks; the demo's provider has no principal, and a fixture that answered 403
 *   itself would make the denied-state tests prove nothing about the page's mirror. The page's handling
 *   of `omitted` / `not-permitted` is tested against injected answers instead.
 */

import type {
  ConfigBundle,
  ConfigChange,
  ConfigProblemLine,
  ConfigRestorePoint,
  ConfigSectionName,
  ConfigSectionReport,
  ConfigValidation,
  MatrixCellWrite,
  ProfileDocument,
} from '../api/types';
import { CONFIG_SECTIONS } from '../api/types';
import { settingsRejection, unknownSettingsDetail, FIXTURE_ROLES } from './identity';
import { cloneMatrix, validateMatrixWrite, type MatrixGrid } from './matrix';
import { diffFixtureProfile, fixtureReleaseBlocking, validateFixtureProfile } from './profile';

export const FIXTURE_CONFIG_FORMAT = 'acs-config-bundle';
export const FIXTURE_CONFIG_VERSION = 1;

/** `config_bundle.RESTORE_POINT_LIMIT`. */
export const FIXTURE_CONFIG_RESTORE_LIMIT = 10;

/* ------------------------------------------------------------------------------------------- */
/* canonical JSON + SHA-256                                                                      */
/* ------------------------------------------------------------------------------------------- */

/** `json.dumps(sort_keys=True, ensure_ascii=False, separators=(",", ":"))` for the JSON the sections carry. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map((item) => canonicalJson(item)).join(',')}]`;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record)
    .filter((key) => record[key] !== undefined)
    .sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(',')}}`;
}

/** UTF-8 bytes of a string, by code point (no `TextEncoder` dependency). */
function utf8(text: string): number[] {
  const out: number[] = [];
  for (const char of text) {
    const cp = char.codePointAt(0) ?? 0;
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    else {
      out.push(
        0xf0 | (cp >> 18),
        0x80 | ((cp >> 12) & 63),
        0x80 | ((cp >> 6) & 63),
        0x80 | (cp & 63),
      );
    }
  }
  return out;
}

/** The first 64 primes — SHA-256's constants are the fractional parts of their cube roots. */
const PRIMES: readonly number[] = (() => {
  const out: number[] = [];
  for (let n = 2; out.length < 64; n += 1) if (out.every((p) => n % p !== 0)) out.push(n);
  return out;
})();

const fraction32 = (x: number): number => Math.floor((x - Math.floor(x)) * 4294967296) >>> 0;
const ROUND_CONSTANTS: readonly number[] = PRIMES.map((p) => fraction32(Math.cbrt(p)));
const INITIAL_HASH: readonly number[] = PRIMES.slice(0, 8).map((p) => fraction32(Math.sqrt(p)));

const rotr = (x: number, n: number): number => ((x >>> n) | (x << (32 - n))) >>> 0;

/** SHA-256 of a string's UTF-8 bytes, lowercase hex. */
export function sha256Hex(text: string): string {
  const bytes = utf8(text);
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (const word of [Math.floor(bitLength / 4294967296), bitLength >>> 0]) {
    for (let shift = 24; shift >= 0; shift -= 8) bytes.push((word >>> shift) & 255);
  }
  const hash = [...INITIAL_HASH];
  const w = new Array<number>(64).fill(0);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) {
      const j = offset + i * 4;
      w[i] =
        ((bytes[j]! << 24) | (bytes[j + 1]! << 16) | (bytes[j + 2]! << 8) | bytes[j + 3]!) >>> 0;
    }
    for (let i = 16; i < 64; i += 1) {
      const a = w[i - 15]!;
      const b = w[i - 2]!;
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = hash as [
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
    ];
    for (let i = 0; i < 64; i += 1) {
      const big1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const choose = (e & f) ^ (~e & g);
      const t1 = (h + big1 + choose + ROUND_CONSTANTS[i]! + w[i]!) >>> 0;
      const big0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (big0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    [a, b, c, d, e, f, g, h].forEach((value, i) => {
      hash[i] = (hash[i]! + value) >>> 0;
    });
  }
  return hash.map((word) => word.toString(16).padStart(8, '0')).join('');
}

/** `config_bundle.digest_of`: identity and integrity of what a bundle SAYS — never of when it was made. */
export function fixtureConfigDigest(sections: unknown): string {
  return `sha256:${sha256Hex(
    canonicalJson({ format: FIXTURE_CONFIG_FORMAT, version: FIXTURE_CONFIG_VERSION, sections }),
  )}`;
}

/* ------------------------------------------------------------------------------------------- */
/* reading the demo's current state as sections                                                  */
/* ------------------------------------------------------------------------------------------- */

/** The three documents a bundle carries, as the provider currently holds them. */
export interface FixtureConfigState {
  /** The six whitelist keys, defaults filled — what `GET /ops/v1/settings` calls `values`. */
  settings: Readonly<Record<string, number>>;
  matrix: MatrixGrid;
  profile: { version: number | null; document: ProfileDocument | null };
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** `config_bundle.read_section`: one section in the shape a bundle carries. */
export function fixtureConfigSection(
  state: FixtureConfigState,
  name: ConfigSectionName,
): Record<string, unknown> {
  if (name === 'ops_settings') return { values: { ...state.settings } };
  if (name === 'permission_matrix') return { capabilities: cloneMatrix(state.matrix) };
  return { active_version: state.profile.version, document: clone(state.profile.document) };
}

/** `Z`-terminated second-precision UTC, the shape `store.utcnow_iso` writes. */
function stamp(at: Date): string {
  return at.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** `config_bundle.export_bundle` — the demo has no principal, so nothing is ever omitted. */
export function fixtureConfigBundle(
  state: FixtureConfigState,
  only: readonly ConfigSectionName[] | undefined,
  at: Date,
  serviceVersion: string,
): ConfigBundle {
  const sections: Record<string, Record<string, unknown>> = {};
  for (const name of only ?? CONFIG_SECTIONS) sections[name] = fixtureConfigSection(state, name);
  return {
    format: FIXTURE_CONFIG_FORMAT,
    version: FIXTURE_CONFIG_VERSION,
    created_at: stamp(at),
    source: { service_version: serviceVersion },
    sections,
    omitted: {},
    digest: fixtureConfigDigest(sections),
  };
}

/* ------------------------------------------------------------------------------------------- */
/* planning                                                                                      */
/* ------------------------------------------------------------------------------------------- */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const line = (path: string, detail: string): ConfigProblemLine => ({ path, detail });

/** Python's `repr` for the scalar shapes a `version` can arrive as — the sentence is the server's. */
function pyRepr(value: unknown): string {
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'string') return `'${value}'`;
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  return typeof value === 'number' ? String(value) : JSON.stringify(value);
}

/** `config_bundle.check_shape`: what is wrong with the bundle as a DOCUMENT. */
export function fixtureCheckBundleShape(bundle: unknown): ConfigProblemLine[] {
  if (!isRecord(bundle)) return [line('bundle', 'expected a JSON object')];
  const found: ConfigProblemLine[] = [];
  if (bundle.format !== FIXTURE_CONFIG_FORMAT) {
    found.push(line('format', `must be '${FIXTURE_CONFIG_FORMAT}'`));
  }
  if (bundle.version !== FIXTURE_CONFIG_VERSION) {
    found.push(
      line(
        'version',
        `this build reads bundle version ${FIXTURE_CONFIG_VERSION}, got ${pyRepr(bundle.version)}`,
      ),
    );
  }
  const sections = bundle.sections;
  if (!isRecord(sections)) {
    found.push(line('sections', 'expected an object'));
    return found;
  }
  const unknown = Object.keys(sections)
    .filter((name) => !(CONFIG_SECTIONS as readonly string[]).includes(name))
    .sort();
  if (unknown.length > 0) found.push(line('sections', `unknown section(s): ${unknown.join(', ')}`));
  if (bundle.digest !== fixtureConfigDigest(sections)) {
    found.push(
      line(
        'digest',
        'does not match the sections — the bundle was edited, truncated or is not what was exported',
      ),
    );
  }
  return found;
}

interface SectionPlan {
  report: ConfigSectionReport;
  /** What the apply step writes for this section — never sent to a client. */
  write?: unknown;
}

const invalid = (path: string, detail: string): SectionPlan => ({
  report: { status: 'invalid', changes: [], problems: [line(path, detail)] },
});

function planSettings(state: FixtureConfigState, doc: unknown): SectionPlan {
  if (!isRecord(doc) || !isRecord(doc.values)) {
    return invalid('ops_settings', 'expected {values: {…}}');
  }
  const values = doc.values;
  const refusal =
    unknownSettingsDetail(values) ??
    Object.keys(values)
      .sort()
      .map((key) => settingsRejection(key, values[key]))
      .find((detail) => detail !== null) ??
    null;
  if (refusal !== null) return invalid('ops_settings', `reference-invalid: ${refusal}`);
  const changes: ConfigChange[] = Object.keys(values)
    .sort()
    .filter((key) => state.settings[key] !== values[key])
    .map((key) => ({
      path: `ops_settings.${key}`,
      before: state.settings[key],
      after: values[key],
    }));
  return {
    report: { status: changes.length > 0 ? 'will-change' : 'unchanged', changes, problems: [] },
    write: Object.fromEntries(changes.map((c) => [c.path.slice('ops_settings.'.length), c.after])),
  };
}

function planMatrix(state: FixtureConfigState, doc: unknown): SectionPlan {
  if (!isRecord(doc) || !isRecord(doc.capabilities)) {
    return invalid('permission_matrix', 'expected {capabilities: {…}}');
  }
  const found: ConfigProblemLine[] = [];
  const cells: MatrixCellWrite[] = [];
  for (const capability of Object.keys(doc.capabilities).sort()) {
    const row = doc.capabilities[capability];
    if (!(capability in state.matrix)) {
      found.push(line(`permission_matrix.${capability}`, 'not a capability in this registry'));
    } else if (!isRecord(row)) {
      found.push(line(`permission_matrix.${capability}`, 'expected {role: level}'));
    } else {
      for (const role of Object.keys(row).sort()) {
        if (!(FIXTURE_ROLES as readonly string[]).includes(role)) {
          found.push(line(`permission_matrix.${capability}.${role}`, 'not a role'));
        } else if (row[role] !== state.matrix[capability]?.[role]) {
          cells.push({ capability, role, level: row[role] as string });
        }
      }
    }
  }
  if (found.length > 0) return { report: { status: 'invalid', changes: [], problems: found } };
  if (cells.length > 0) {
    const judged = validateMatrixWrite(state.matrix, cells);
    if (!judged.ok) return invalid('permission_matrix', `${judged.code}: ${judged.detail}`);
  }
  const changes: ConfigChange[] = cells.map((cell) => ({
    path: `permission_matrix.${cell.capability}.${cell.role}`,
    before: state.matrix[cell.capability]?.[cell.role],
    after: cell.level,
  }));
  return {
    report: { status: changes.length > 0 ? 'will-change' : 'unchanged', changes, problems: [] },
    write: cells,
  };
}

function planProfile(state: FixtureConfigState, doc: unknown): SectionPlan {
  if (!isRecord(doc) || !('document' in doc)) {
    return invalid('operator_profile', 'expected {document: …}');
  }
  if (doc.document === null) {
    return { report: { status: 'absent', changes: [], problems: [] } }; // the source had nothing activated
  }
  const [parsed, found] = validateFixtureProfile(doc.document);
  if (parsed === null) {
    const blocked = fixtureReleaseBlocking(found);
    const lines = found.map((p) =>
      line(`operator_profile.${p.path}`.replace(/\.+$/, ''), `${p.rule}: ${p.detail}`),
    );
    if (blocked.length > 0 && JSON.stringify(blocked) === JSON.stringify(found)) {
      lines.unshift(
        line('operator_profile', 'release-blocked: this deployment would refuse to activate it'),
      );
    }
    return { report: { status: 'invalid', changes: [], problems: lines } };
  }
  const active = state.profile.document;
  if (active !== null && canonicalJson(parsed) === canonicalJson(active)) {
    return { report: { status: 'unchanged', changes: [], problems: [] } };
  }
  return {
    report: {
      status: 'will-change',
      changes: diffFixtureProfile(active, parsed).map((c) => ({
        path: `operator_profile.${c.path}`,
        before: c.before ?? null,
        after: c.after ?? null,
      })),
      problems: [],
    },
    write: { document: parsed, expected_version: state.profile.version ?? 0 },
  };
}

const PLANNERS: Record<
  ConfigSectionName,
  (state: FixtureConfigState, doc: unknown) => SectionPlan
> = {
  ops_settings: planSettings,
  permission_matrix: planMatrix,
  operator_profile: planProfile,
};

/** What the apply step writes, per section — the server's `_apply` payloads. */
export interface FixtureConfigWrites {
  ops_settings?: Record<string, number>;
  permission_matrix?: MatrixCellWrite[];
  operator_profile?: { document: ProfileDocument; expected_version: number };
}

/**
 * `config_bundle.plan`: judge a bundle against the demo's CURRENT state and write nothing. `ok` is true
 * only when the document is sound and no section is `invalid` — the same predicate as the server's.
 */
export function fixtureConfigPlan(
  state: FixtureConfigState,
  bundle: unknown,
  wanted: readonly ConfigSectionName[] = CONFIG_SECTIONS,
): { validation: ConfigValidation; writes: FixtureConfigWrites } {
  const documentProblems = fixtureCheckBundleShape(bundle);
  const sectionsIn: Record<string, unknown> =
    isRecord(bundle) && isRecord(bundle.sections) ? bundle.sections : {};
  const report: Record<string, ConfigSectionReport> = {};
  const writes: FixtureConfigWrites = {};
  for (const name of wanted) {
    if (!(name in sectionsIn)) {
      report[name] = { status: 'absent', changes: [], problems: [] };
      continue;
    }
    const plan = PLANNERS[name](state, sectionsIn[name]);
    report[name] = plan.report;
    if (plan.write !== undefined) (writes as Record<string, unknown>)[name] = plan.write;
  }
  const ok =
    documentProblems.length === 0 &&
    Object.values(report).every((r) => r.status !== 'invalid' && r.status !== 'not-permitted');
  return {
    validation: {
      ok,
      digest: isRecord(bundle) && typeof bundle.digest === 'string' ? bundle.digest : null,
      problems: documentProblems,
      sections: report,
    },
    writes,
  };
}

/* ------------------------------------------------------------------------------------------- */
/* restore points                                                                                */
/* ------------------------------------------------------------------------------------------- */

/** One stored restore point: the public row beside the bundle it carries. */
export interface FixtureRestorePoint {
  point: ConfigRestorePoint;
  bundle: ConfigBundle;
}

/**
 * `config_bundle.take_restore_point`'s document: the CURRENT state of exactly the sections a restore is
 * about to overwrite, as an ordinary bundle — so it goes back through `:validate` and `:apply` like any
 * file.
 */
export function fixtureRestorePoint(
  pointId: string,
  state: FixtureConfigState,
  sections: readonly ConfigSectionName[],
  actor: string,
  at: Date,
  serviceVersion: string,
): FixtureRestorePoint {
  const bundle = fixtureConfigBundle(state, sections, at, serviceVersion);
  return {
    point: {
      point_id: pointId,
      created_at: stamp(at),
      actor,
      actor_kind: 'human',
      sections: [...sections],
      digest: bundle.digest,
    },
    bundle,
  };
}
